import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import {
    Sparkles, HardHat, Trash2, AlertTriangle, Loader2, Plus,
    ShieldCheck, Stamp, Clock, Zap, CreditCard, CheckCircle2, ArrowRight, Info,
    Receipt, Download, Mail, UserCheck, FileBadge,
} from 'lucide-react';

const LIVELLO_COLORS = {
    basso: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
    medio: 'bg-[hsl(45_100%_92%)] text-[hsl(220_13%_10%)]',
    alto: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
};

const URGENZA_OPTIONS = [
    { value: 'standard', icon: Clock, color: 'text-[hsl(var(--muted-foreground))]' },
    { value: 'veloce',   icon: Zap,   color: 'text-[hsl(35_90%_45%)]' },
    { value: 'urgente',  icon: Zap,   color: 'text-[hsl(0_72%_52%)]' },
];

export default function Sicurezza() {
    const { t } = useTranslation();
    const [docs, setDocs] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [openDetail, setOpenDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        tipologia_lavoro: '',
        descrizione: '',
        altezza_metri: '',
        n_lavoratori: '',
        durata_giorni: '',
        luogo: '',
    });

    // Firma professionale state
    const [firmaOpen, setFirmaOpen] = useState(false);
    const [firmaStep, setFirmaStep] = useState(1); // 1 = form, 2 = preview/checkout
    const [firmaForm, setFirmaForm] = useState({
        descrizione_lavoro: '',
        urgenza: 'standard',
        n_lavoratori: '',
        durata_giorni: '',
        altezza_metri: '',
        contatto_email: '',
        contatto_telefono: '',
    });
    const [firmaQuote, setFirmaQuote] = useState(null);
    const [firmaLoading, setFirmaLoading] = useState(false);

    // Map: dvr_id -> firma_status payload (or null if no firma yet)
    const [firmaStatuses, setFirmaStatuses] = useState({});
    // Status of the currently-open DVR (for the detail dialog)
    const [detailFirmaStatus, setDetailFirmaStatus] = useState(null);

    const load = async () => {
        const res = await api.get('/sicurezza/list');
        setDocs(res.data);
        // Fetch firma status for each DVR in parallel (small N, OK)
        try {
            const statuses = await Promise.all(
                (res.data || []).map((d) =>
                    api.get(`/sicurezza/${d.id}/firma-status`).then((r) => [d.id, r.data]).catch(() => [d.id, null])
                )
            );
            const map = {};
            statuses.forEach(([id, st]) => { map[id] = st; });
            setFirmaStatuses(map);
        } catch (e) {
            console.error('[Sicurezza] firma-status batch failed:', e);
        }
    };
    useEffect(() => { load(); }, []);

    // Load detail firma status when opening detail dialog
    useEffect(() => {
        if (!openDetail?.id) { setDetailFirmaStatus(null); return; }
        // Use cached if present, else fetch
        if (firmaStatuses[openDetail.id] !== undefined) {
            setDetailFirmaStatus(firmaStatuses[openDetail.id]);
        }
        api.get(`/sicurezza/${openDetail.id}/firma-status`).then((r) => {
            setDetailFirmaStatus(r.data);
            setFirmaStatuses((prev) => ({ ...prev, [openDetail.id]: r.data }));
        }).catch(() => {});
    }, [openDetail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle Stripe success/cancel redirect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('firma_success') === '1') {
            const sid = params.get('session_id');
            if (sid) {
                // Trigger sync confirmation (in case webhook is delayed)
                api.get(`/payments/checkout/status/${sid}`).catch(() => {});
            }
            toast.success(t('pages.sec_firma_success_toast') || 'Pagamento ricevuto! Un nostro professionista ti contatterà a breve.', { duration: 6000 });
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('firma_cancel') === '1') {
            toast.info(t('pages.sec_firma_cancel_toast') || 'Pagamento annullato');
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [t]);

    const generate = async (e) => {
        e.preventDefault();
        if (!form.tipologia_lavoro || !form.descrizione) { toast.error(t('pages.sec_toast_fill')); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                altezza_metri: form.altezza_metri ? parseFloat(form.altezza_metri) : null,
                n_lavoratori: form.n_lavoratori ? parseInt(form.n_lavoratori) : null,
                durata_giorni: form.durata_giorni ? parseInt(form.durata_giorni) : null,
            };
            const res = await api.post('/sicurezza/generate', payload);
            toast.success(t('pages.sec_toast_generated'));
            setOpenForm(false);
            setOpenDetail(res.data);
            load();
            setForm({ tipologia_lavoro: '', descrizione: '', altezza_metri: '', n_lavoratori: '', durata_giorni: '', luogo: '' });
        } catch (e) {
            toast.error(t('common.generic_error'));
        } finally { setLoading(false); }
    };

    const remove = async (id) => {
        if (!window.confirm(t('common.confirm_delete'))) return;
        await api.delete(`/sicurezza/${id}`);
        load();
    };

    // === FIRMA PROFESSIONALE FLOW ===
    const openFirmaDialog = () => {
        // Pre-fill from current DVR parameters
        const p = openDetail?.parametri || {};
        setFirmaForm({
            descrizione_lavoro: '',
            urgenza: 'standard',
            n_lavoratori: p.n_lavoratori || '',
            durata_giorni: p.durata_giorni || '',
            altezza_metri: p.altezza_metri || '',
            contatto_email: '',
            contatto_telefono: '',
        });
        setFirmaQuote(null);
        setFirmaStep(1);
        setFirmaOpen(true);
    };

    const requestPricing = async (e) => {
        e?.preventDefault();
        if (!openDetail?.id) return;
        if (!firmaForm.descrizione_lavoro || firmaForm.descrizione_lavoro.length < 20) {
            toast.error(t('pages.sec_firma_desc_min') || 'Descrivi il lavoro con almeno 20 caratteri');
            return;
        }
        setFirmaLoading(true);
        try {
            const payload = {
                dvr_id: openDetail.id,
                descrizione_lavoro: firmaForm.descrizione_lavoro,
                urgenza: firmaForm.urgenza,
                n_lavoratori: firmaForm.n_lavoratori ? parseInt(firmaForm.n_lavoratori) : null,
                durata_giorni: firmaForm.durata_giorni ? parseInt(firmaForm.durata_giorni) : null,
                altezza_metri: firmaForm.altezza_metri ? parseFloat(firmaForm.altezza_metri) : null,
            };
            const res = await api.post('/sicurezza/firma/pricing', payload);
            setFirmaQuote(res.data);
            setFirmaStep(2);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setFirmaLoading(false); }
    };

    const proceedToCheckout = async () => {
        if (!firmaQuote || !openDetail?.id) return;
        setFirmaLoading(true);
        try {
            const payload = {
                dvr_id: openDetail.id,
                descrizione_lavoro: firmaForm.descrizione_lavoro,
                urgenza: firmaQuote.urgenza,
                complessita: firmaQuote.complessita,
                prezzo_totale: firmaQuote.prezzo_totale,
                origin_url: window.location.origin,
                n_lavoratori: firmaForm.n_lavoratori ? parseInt(firmaForm.n_lavoratori) : null,
                durata_giorni: firmaForm.durata_giorni ? parseInt(firmaForm.durata_giorni) : null,
                altezza_metri: firmaForm.altezza_metri ? parseFloat(firmaForm.altezza_metri) : null,
                contatto_email: firmaForm.contatto_email || null,
                contatto_telefono: firmaForm.contatto_telefono || null,
            };
            const res = await api.post('/sicurezza/firma/checkout', payload);
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                toast.error(t('common.generic_error'));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setFirmaLoading(false); }
    };

    const complColors = {
        easy:    'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)] border-[hsl(145_55%_70%)]',
        medium:  'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] border-[hsl(45_100%_60%)]',
        complex: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)] border-[hsl(0_72%_70%)]',
    };

    // Status badge config (single source of truth for both card and detail)
    const STATO_BADGE = {
        pagata:         { label: t('pages.sec_st_pagata',   'PAGATA · firma richiesta'),       cls: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] border-[hsl(45_100%_60%)]', icon: Receipt },
        in_lavorazione: { label: t('pages.sec_st_in_lav',   'IN LAVORAZIONE'),                 cls: 'bg-[hsl(35_100%_94%)] text-[hsl(25_85%_42%)] border-[hsl(35_90%_55%)]', icon: UserCheck },
        completata:     { label: t('pages.sec_st_compl',    'FIRMATA · pronta'),               cls: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)] border-[hsl(145_55%_70%)]', icon: CheckCircle2 },
        pending:        { label: t('pages.sec_st_pending',  'IN ATTESA PAGAMENTO'),            cls: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]', icon: Clock },
    };
    const renderStatoBadge = (st, size = 'sm') => {
        const cfg = STATO_BADGE[st?.stato];
        if (!cfg) return null;
        const Icon = cfg.icon;
        const sizeCls = size === 'lg' ? 'text-xs px-3 py-1.5' : 'text-[10px] px-2 py-0.5';
        return (
            <Badge className={`${cfg.cls} ${sizeCls} gap-1 border font-bold`} data-testid={`firma-badge-${st.stato}`}>
                <Icon className="w-3 h-3" /> {cfg.label}
            </Badge>
        );
    };

    const downloadRicevuta = async (firmaId, numero) => {
        try {
            const r = await api.get(`/firma/${firmaId}/ricevuta`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Ricevuta_${numero || firmaId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const downloadDraftDvr = async (dvrId, tipologia) => {
        // Owner-side: download the AI-generated provisional draft (with BOZZA watermark)
        try {
            const r = await api.get(`/sicurezza/${dvrId}/draft-pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            const safe = (tipologia || 'DVR').replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 40);
            a.download = `Bozza_DVR_${safe}_${dvrId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const downloadSignedDvr = async (firmaId) => {
        // Owner-side download of the signed PDF (admin endpoint allows owner too)
        try {
            const r = await api.get(`/admin/firma/${firmaId}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `DVR_firmato_${firmaId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    return (
        <div data-testid="sicurezza-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl">{t('pages.sec_title')}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.sec_subtitle')}</p>
                </div>
                <Button onClick={() => setOpenForm(true)} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="sicurezza-new-button">
                    <Sparkles className="w-4 h-4" /> {t('pages.sec_generate')}
                </Button>
            </div>

            {/* Top-level legal info banner */}
            <Alert className="mb-6 border-[hsl(45_100%_60%)] bg-[hsl(45_100%_96%)]" data-testid="sicurezza-legal-banner">
                <Info className="h-4 w-4 text-[hsl(35_90%_35%)]" />
                <AlertDescription className="text-sm text-[hsl(220_13%_10%)]">
                    <strong>{t('pages.sec_legal_banner_title') || 'Importante:'}</strong> {t('pages.sec_legal_banner_text') || 'I documenti generati sono BOZZE TECNICHE basate sul D.Lgs 81/2008. Per la validità legale devono essere revisionati e firmati da un professionista abilitato (RSPP / CSP / CSE). CantiereAI offre questo servizio tramite il proprio network certificato.'}
                </AlertDescription>
            </Alert>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((d) => {
                    const fs = firmaStatuses[d.id];
                    return (
                    <Card key={d.id} className="p-5 hover:shadow-lg transition-colors cursor-pointer" onClick={() => setOpenDetail(d)} data-testid={`sicurezza-card-${d.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <HardHat className="w-8 h-8 text-[hsl(var(--accent))]" />
                            {fs && renderStatoBadge(fs, 'sm')}
                        </div>
                        <h3 className="font-semibold mb-1">{d.tipologia_lavoro}</h3>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mb-2">{d.descrizione}</p>
                        <div className="flex gap-1 flex-wrap">
                            {(d.valutazione?.rischi || []).slice(0, 3).map((r, i) => <Badge key={i} className={LIVELLO_COLORS[r.livello]}>{r.livello}</Badge>)}
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-[hsl(var(--border))]">
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sec_card_risks', { n: d.valutazione?.rischi?.length || 0 })}</span>
                            <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} className="text-[hsl(var(--destructive))] hover:text-[hsl(0_72%_40%)]" data-testid={`sicurezza-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {/* Mini disclaimer - only if NO firma yet */}
                        {!fs && (
                            <div className="mt-2 pt-2 border-t border-dashed border-[hsl(var(--border))] flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{t('pages.sec_card_disclaimer', 'Bozza tecnica · richiede firma professionale per validità legale')}</span>
                            </div>
                        )}
                    </Card>
                    );
                })}
                {docs.length === 0 && (
                    <Card className="p-10 text-center col-span-full text-[hsl(var(--muted-foreground))]">
                        <HardHat className="w-12 h-12 mx-auto mb-3" />
                        <p className="mb-3">{t('pages.sec_empty')}</p>
                        <Button onClick={() => setOpenForm(true)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]"><Plus className="w-4 h-4 mr-1" /> {t('pages.sec_empty_cta')}</Button>
                    </Card>
                )}
            </div>

            {/* CREATE DVR DIALOG */}
            <Dialog open={openForm} onOpenChange={setOpenForm}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle className="font-display text-2xl">{t('pages.sec_form_title')}</DialogTitle></DialogHeader>
                    <form onSubmit={generate} className="space-y-4 mt-2">
                        <div><Label>{t('pages.sec_form_type')}</Label><Input required value={form.tipologia_lavoro} onChange={(e) => setForm({...form, tipologia_lavoro: e.target.value})} placeholder={t('pages.sec_form_type_ph')} className="mt-1" data-testid="sicurezza-tipologia" /></div>
                        <div><Label>{t('pages.sec_form_desc')}</Label><Textarea required rows={3} value={form.descrizione} onChange={(e) => setForm({...form, descrizione: e.target.value})} placeholder={t('pages.sec_form_desc_ph')} className="mt-1" /></div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label>{t('pages.sec_form_height')}</Label><Input type="number" value={form.altezza_metri} onChange={(e) => setForm({...form, altezza_metri: e.target.value})} className="mt-1" /></div>
                            <div><Label>{t('pages.sec_form_workers')}</Label><Input type="number" value={form.n_lavoratori} onChange={(e) => setForm({...form, n_lavoratori: e.target.value})} className="mt-1" /></div>
                            <div><Label>{t('pages.sec_form_days')}</Label><Input type="number" value={form.durata_giorni} onChange={(e) => setForm({...form, durata_giorni: e.target.value})} className="mt-1" /></div>
                        </div>
                        <div><Label>{t('pages.sec_form_place')}</Label><Input value={form.luogo} onChange={(e) => setForm({...form, luogo: e.target.value})} className="mt-1" /></div>
                        <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold h-11" data-testid="sicurezza-generate-button">
                            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.generating')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('common.generate_ai')}</>}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* DVR DETAIL DIALOG */}
            <Dialog open={!!openDetail} onOpenChange={() => setOpenDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sicurezza-detail-dialog">
                    {openDetail && (
                        <div>
                            <DialogHeader><DialogTitle className="font-display text-2xl">{openDetail.tipologia_lavoro}</DialogTitle></DialogHeader>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{openDetail.descrizione}</p>

                            {/* === FIRMA PROFESSIONALE STATUS === */}
                            {detailFirmaStatus ? (
                                <Card className={`mb-5 p-5 border-2 ${detailFirmaStatus.stato === 'completata' ? 'bg-[hsl(145_45%_97%)] border-[hsl(145_55%_70%)]' : detailFirmaStatus.stato === 'in_lavorazione' ? 'bg-[hsl(35_100%_97%)] border-[hsl(35_90%_55%)]' : 'bg-[hsl(45_100%_97%)] border-[hsl(45_100%_60%)]'}`} data-testid="sicurezza-detail-status-card">
                                    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                        <div className="flex items-center gap-2">
                                            <Stamp className="w-5 h-5 text-[hsl(var(--accent))]" />
                                            <span className="font-display text-lg">{t('pages.sec_status_title', 'Pratica firma professionale')}</span>
                                        </div>
                                        {renderStatoBadge(detailFirmaStatus, 'lg')}
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-3 mb-3">
                                        <div className="p-3 rounded-lg bg-white/60">
                                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.sec_status_price', 'Importo pagato')}</div>
                                            <div className="font-display text-xl tabular-nums">€{Number(detailFirmaStatus.prezzo_totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{t('pages.sec_status_iva_inc', 'IVA 22% inclusa')}</div>
                                        </div>
                                        <div className="p-3 rounded-lg bg-white/60">
                                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.sec_status_paid_at', 'Pagato il')}</div>
                                            <div className="text-sm font-semibold">{detailFirmaStatus.paid_at ? new Date(detailFirmaStatus.paid_at).toLocaleDateString('it-IT') : '-'}</div>
                                            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{detailFirmaStatus.urgenza?.toUpperCase()}</div>
                                        </div>
                                        <div className="p-3 rounded-lg bg-white/60">
                                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.sec_status_pro', 'Professionista')}</div>
                                            {detailFirmaStatus.professionista ? (
                                                <>
                                                    <div className="text-sm font-semibold truncate">{detailFirmaStatus.professionista.nome}</div>
                                                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{detailFirmaStatus.professionista.qualifica}</div>
                                                </>
                                            ) : (
                                                <div className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">{t('pages.sec_status_pro_pending', 'In assegnazione')}</div>
                                            )}
                                        </div>
                                    </div>

                                    {detailFirmaStatus.stato === 'pagata' && (
                                        <div className="text-sm text-[hsl(35_90%_35%)] flex items-start gap-2 mb-3">
                                            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>{t('pages.sec_status_msg_pagata', 'La tua richiesta è in coda di assegnazione. Un professionista certificato la prenderà in carico a breve e ti contatterà via email.')}</span>
                                        </div>
                                    )}
                                    {detailFirmaStatus.stato === 'in_lavorazione' && (
                                        <div className="text-sm text-[hsl(25_85%_42%)] flex items-start gap-2 mb-3">
                                            <UserCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>{(detailFirmaStatus.professionista?.nome || 'Il professionista')} {t('pages.sec_status_msg_in_lav', 'sta revisionando il documento. Riceverai il PDF firmato via email entro lo SLA.')}</span>
                                        </div>
                                    )}
                                    {detailFirmaStatus.stato === 'completata' && (
                                        <div className="text-sm text-[hsl(145_55%_34%)] flex items-start gap-2 mb-3">
                                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>{t('pages.sec_status_msg_completata', 'DVR firmato consegnato')}{detailFirmaStatus.delivered_to ? ' · ' + detailFirmaStatus.delivered_to : ''}. {t('pages.sec_status_msg_completata2', 'Conserva il PDF insieme alla documentazione di cantiere.')}</span>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-2 pt-3 border-t border-[hsl(var(--border))]/40">
                                        <Button
                                            onClick={() => downloadDraftDvr(openDetail.id, openDetail.tipologia_lavoro)}
                                            variant="outline" size="sm" className="gap-2 bg-white"
                                            data-testid="sicurezza-download-draft"
                                        >
                                            <Download className="w-3.5 h-3.5" /> {t('pages.sec_dl_bozza', 'Scarica bozza (AI)')}
                                        </Button>
                                        <Button
                                            onClick={() => downloadRicevuta(detailFirmaStatus.id, detailFirmaStatus.ricevuta_numero)}
                                            variant="outline" size="sm" className="gap-2 bg-white"
                                            data-testid="sicurezza-download-ricevuta"
                                        >
                                            <Receipt className="w-3.5 h-3.5" /> {t('pages.sec_dl_ricevuta', 'Scarica ricevuta')}{detailFirmaStatus.ricevuta_numero ? ` (${detailFirmaStatus.ricevuta_numero})` : ''}
                                        </Button>
                                        {detailFirmaStatus.has_signed_pdf && (
                                            <Button
                                                onClick={() => downloadSignedDvr(detailFirmaStatus.id)}
                                                size="sm" className="gap-2 bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white"
                                                data-testid="sicurezza-download-dvr-firmato"
                                            >
                                                <FileBadge className="w-3.5 h-3.5" /> {t('pages.sec_dl_dvr_firmato', 'Scarica DVR firmato')}
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            ) : (
                                /* No practice yet: show disclaimer + CTA */
                                <Alert className="mb-5 border-[hsl(45_100%_60%)] bg-[hsl(45_100%_96%)]" data-testid="sicurezza-detail-disclaimer">
                                    <AlertTriangle className="h-5 w-5 text-[hsl(35_90%_35%)]" />
                                    <AlertDescription className="text-sm">
                                        <div className="font-semibold mb-1 text-[hsl(220_13%_10%)]">{t('pages.sec_detail_disclaimer_title') || 'Bozza tecnica — NON valida legalmente senza firma professionale'}</div>
                                        <div className="text-[hsl(220_13%_25%)]">{t('pages.sec_detail_disclaimer_body') || 'Questo documento è una bozza tecnica generata dall\'AI sulla base del D.Lgs 81/2008. Per avere validità legale deve essere revisionato e firmato da un professionista abilitato (RSPP / CSP / CSE). CantiereAI offre il servizio di firma professionale tramite il proprio network certificato.'}</div>
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <Button
                                                onClick={() => downloadDraftDvr(openDetail.id, openDetail.tipologia_lavoro)}
                                                variant="outline"
                                                className="bg-white gap-2 font-semibold"
                                                data-testid="sicurezza-download-draft-empty"
                                            >
                                                <Download className="w-4 h-4" />
                                                {t('pages.sec_dl_bozza', 'Scarica bozza (AI)')}
                                            </Button>
                                            <Button
                                                onClick={openFirmaDialog}
                                                className="bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white gap-2 font-semibold"
                                                data-testid="sicurezza-request-firma-button"
                                            >
                                                <Stamp className="w-4 h-4" />
                                                {t('pages.sec_firma_cta') || 'Richiedi firma professionale'}
                                                <ArrowRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Accordion type="multiple" className="space-y-2">
                                {(openDetail.valutazione?.rischi || []).map((r, i) => (
                                    <AccordionItem key={i} value={`r-${i}`} className="border rounded-lg px-4">
                                        <AccordionTrigger className="hover:no-underline">
                                            <div className="flex items-center gap-2 text-left">
                                                <AlertTriangle className="w-4 h-4 text-[hsl(var(--accent))]" />
                                                <span className="font-semibold">{r.rischio}</span>
                                                <Badge className={LIVELLO_COLORS[r.livello] + ' ml-2'}>{r.livello}</Badge>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <div className="font-semibold mb-1">{t('pages.sec_detail_prevention')}</div>
                                                    <ul className="list-disc pl-4 space-y-0.5 text-[hsl(var(--muted-foreground))]">
                                                        {(r.misure_prevenzione || []).map((m, j) => <li key={j}>{m}</li>)}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <div className="font-semibold mb-1">{t('pages.sec_detail_ppe')}</div>
                                                    <ul className="list-disc pl-4 space-y-0.5 text-[hsl(var(--muted-foreground))]">
                                                        {(r.dpi_richiesti || []).map((d, j) => <li key={j}>{d}</li>)}
                                                    </ul>
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>

                            <div className="grid md:grid-cols-2 gap-4 mt-5 pt-4 border-t">
                                <div>
                                    <div className="font-semibold text-sm mb-2">{t('pages.sec_detail_training')}</div>
                                    <ul className="text-sm space-y-1 text-[hsl(var(--muted-foreground))]">
                                        {(openDetail.valutazione?.formazione_richiesta || []).map((f, i) => <li key={i}>• {f}</li>)}
                                    </ul>
                                </div>
                                <div>
                                    <div className="font-semibold text-sm mb-2">{t('pages.sec_detail_docs')}</div>
                                    <ul className="text-sm space-y-1 text-[hsl(var(--muted-foreground))]">
                                        {(openDetail.valutazione?.documenti_obbligatori || []).map((d, i) => <li key={i}>• {d}</li>)}
                                    </ul>
                                </div>
                            </div>
                            {openDetail.valutazione?.note && <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">{openDetail.valutazione.note}</p>}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* FIRMA PROFESSIONALE DIALOG (2 steps) */}
            <Dialog open={firmaOpen} onOpenChange={(o) => { setFirmaOpen(o); if (!o) { setFirmaStep(1); setFirmaQuote(null); } }}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sicurezza-firma-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <Stamp className="w-6 h-6 text-[hsl(var(--accent))]" />
                            {t('pages.sec_firma_dialog_title') || 'Richiedi firma professionale DVR'}
                        </DialogTitle>
                    </DialogHeader>

                    {firmaStep === 1 && (
                        <form onSubmit={requestPricing} className="space-y-4 mt-2" data-testid="sicurezza-firma-step1">
                            <Alert className="bg-[hsl(210_90%_96%)] border-[hsl(210_90%_70%)]">
                                <ShieldCheck className="h-4 w-4 text-[hsl(210_90%_40%)]" />
                                <AlertDescription className="text-sm">
                                    {t('pages.sec_firma_intro') || 'Un professionista abilitato (RSPP / CSP / CSE) revisiona la bozza, ti contatta per eventuali integrazioni, firma il documento e lo restituisce con timbro e firma digitale. Il prezzo viene calcolato dall\'AI in base alla complessità del lavoro.'}
                                </AlertDescription>
                            </Alert>

                            <div>
                                <Label>{t('pages.sec_firma_desc_label') || 'Descrivi il lavoro nel dettaglio'} <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                <Textarea
                                    required
                                    rows={4}
                                    value={firmaForm.descrizione_lavoro}
                                    onChange={(e) => setFirmaForm({ ...firmaForm, descrizione_lavoro: e.target.value })}
                                    placeholder={t('pages.sec_firma_desc_ph') || 'Es: Ristrutturazione bagno 8mq al 3° piano, demolizione muratura non portante, rifacimento impianti idraulico e elettrico, 4 operai, lavorazioni a parete fino 2,5m...'}
                                    className="mt-1"
                                    data-testid="firma-descrizione"
                                />
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('pages.sec_firma_desc_hint') || 'Più dettagli fornisci, più accurato sarà il preventivo (l\'AI valuta complessità tecnica e necessità di sopralluogo).'}</p>
                            </div>

                            <div>
                                <Label>{t('pages.sec_firma_urgency_label') || 'Urgenza'} <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                <div className="grid grid-cols-3 gap-2 mt-1">
                                    {URGENZA_OPTIONS.map((u) => {
                                        const Icon = u.icon;
                                        const active = firmaForm.urgenza === u.value;
                                        return (
                                            <button
                                                key={u.value}
                                                type="button"
                                                onClick={() => setFirmaForm({ ...firmaForm, urgenza: u.value })}
                                                className={`p-3 rounded-lg border-2 text-left transition-colors ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50'}`}
                                                data-testid={`firma-urgency-${u.value}`}
                                            >
                                                <Icon className={`w-4 h-4 mb-1 ${u.color}`} />
                                                <div className="font-semibold text-sm">{t(`pages.sec_firma_urg_${u.value}`) || u.value}</div>
                                                <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{t(`pages.sec_firma_urg_${u.value}_sla`)}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-xs">{t('pages.sec_form_workers')}</Label>
                                    <Input type="number" value={firmaForm.n_lavoratori} onChange={(e) => setFirmaForm({ ...firmaForm, n_lavoratori: e.target.value })} className="mt-1" data-testid="firma-workers" />
                                </div>
                                <div>
                                    <Label className="text-xs">{t('pages.sec_form_days')}</Label>
                                    <Input type="number" value={firmaForm.durata_giorni} onChange={(e) => setFirmaForm({ ...firmaForm, durata_giorni: e.target.value })} className="mt-1" data-testid="firma-days" />
                                </div>
                                <div>
                                    <Label className="text-xs">{t('pages.sec_form_height')}</Label>
                                    <Input type="number" step="0.1" value={firmaForm.altezza_metri} onChange={(e) => setFirmaForm({ ...firmaForm, altezza_metri: e.target.value })} className="mt-1" data-testid="firma-height" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">{t('pages.sec_firma_contact_email') || 'Email contatto (opzionale)'}</Label>
                                    <Input type="email" value={firmaForm.contatto_email} onChange={(e) => setFirmaForm({ ...firmaForm, contatto_email: e.target.value })} className="mt-1" placeholder={t('pages.sec_firma_contact_email_ph') || 'lasciare vuoto per usare email account'} data-testid="firma-email" />
                                </div>
                                <div>
                                    <Label className="text-xs">{t('pages.sec_firma_contact_phone') || 'Telefono (opzionale)'}</Label>
                                    <Input value={firmaForm.contatto_telefono} onChange={(e) => setFirmaForm({ ...firmaForm, contatto_telefono: e.target.value })} className="mt-1" placeholder="+39..." data-testid="firma-phone" />
                                </div>
                            </div>

                            <Button type="submit" disabled={firmaLoading} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold h-11" data-testid="firma-pricing-button">
                                {firmaLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('pages.sec_firma_pricing_loading') || 'L\'AI sta valutando la complessità...'}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('pages.sec_firma_get_quote') || 'Calcola preventivo con AI'}</>}
                            </Button>
                        </form>
                    )}

                    {firmaStep === 2 && firmaQuote && (
                        <div className="space-y-4 mt-2" data-testid="sicurezza-firma-step2">
                            {/* Price box */}
                            <Card className={`p-5 border-2 ${complColors[firmaQuote.complessita]}`} data-testid="firma-price-card">
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <Badge className={`${complColors[firmaQuote.complessita]} font-bold text-xs px-3 py-1`}>
                                        {t(`pages.sec_firma_compl_${firmaQuote.complessita}`) || firmaQuote.complessita_label}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs gap-1">
                                        <Clock className="w-3 h-3" /> {firmaQuote.sla}
                                    </Badge>
                                </div>
                                <div className="font-display text-4xl tabular-nums mb-1" data-testid="firma-total-price">
                                    €{Number(firmaQuote.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                    {t('pages.sec_firma_price_breakdown', { net: Number(firmaQuote.prezzo_netto).toLocaleString('it-IT', { minimumFractionDigits: 2 }), iva: Number(firmaQuote.iva).toLocaleString('it-IT', { minimumFractionDigits: 2 }) }) || `Imponibile €${firmaQuote.prezzo_netto} + IVA 22% €${firmaQuote.iva}`}
                                </div>
                            </Card>

                            {/* AI explanation */}
                            <div className="space-y-2">
                                <div className="flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                    <div className="text-sm">
                                        <div className="font-semibold mb-1">{t('pages.sec_firma_ai_reasoning') || 'Valutazione AI:'}</div>
                                        <div className="text-[hsl(var(--muted-foreground))]">{firmaQuote.motivazione_ai}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Service details */}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="p-3 rounded-lg bg-[hsl(var(--muted))]/40">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sec_firma_professional') || 'Professionista'}</div>
                                    <div className="font-semibold">{firmaQuote.professionista_consigliato}</div>
                                </div>
                                <div className="p-3 rounded-lg bg-[hsl(var(--muted))]/40">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.sec_firma_inspection') || 'Sopralluogo'}</div>
                                    <div className="font-semibold">{firmaQuote.richiede_sopralluogo ? t('common.yes', 'Sì') : t('common.no', 'No')}</div>
                                </div>
                            </div>

                            {/* What's included */}
                            <div className="p-4 rounded-lg bg-[hsl(var(--muted))]/30 text-sm space-y-1">
                                <div className="font-semibold mb-2">{t('pages.sec_firma_included') || 'Cosa include:'}</div>
                                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {t('pages.sec_firma_inc_1') || 'Revisione completa del DVR da professionista abilitato'}</div>
                                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {t('pages.sec_firma_inc_2') || 'Integrazioni tecniche e adeguamento normativo D.Lgs 81/2008'}</div>
                                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {t('pages.sec_firma_inc_3') || 'Firma digitale + timbro professionale (validità legale)'}</div>
                                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {t('pages.sec_firma_inc_4') || 'Restituzione PDF firmato via email entro SLA'}</div>
                                {firmaQuote.richiede_sopralluogo && <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {t('pages.sec_firma_inc_5') || 'Sopralluogo in cantiere (incluso nel prezzo)'}</div>}
                            </div>

                            <DialogFooter className="flex flex-col sm:flex-row gap-2">
                                <Button variant="outline" onClick={() => setFirmaStep(1)} disabled={firmaLoading} data-testid="firma-back-button">
                                    {t('common.back') || 'Indietro'}
                                </Button>
                                <Button
                                    onClick={proceedToCheckout}
                                    disabled={firmaLoading}
                                    className="bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white font-bold gap-2 flex-1"
                                    data-testid="firma-checkout-button"
                                >
                                    {firmaLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading') || 'Caricamento'}</> : <><CreditCard className="w-4 h-4" /> {t('pages.sec_firma_pay_cta', { amount: Number(firmaQuote.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 }) }) || `Paga €${firmaQuote.prezzo_totale} e procedi`}</>}
                                </Button>
                            </DialogFooter>

                            <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center">
                                {t('pages.sec_firma_payment_secure') || 'Pagamento sicuro Stripe · Carta di Credito · Apple Pay · Google Pay'}
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
